import { create } from 'zustand';
import type { AssetDto } from '@indusketch/shared';
import { assetsApi } from '../../../lib/api/endpoints.js';
import { userMessage } from '../../../lib/errors.js';
import { currentI18n } from '../../../i18n/i18n.js';

/** Must match the server default; checked early to avoid a pointless upload. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

interface AssetState {
  assets: Record<string, AssetDto>;
  loaded: boolean;
  uploading: boolean;
  error: string | null;
  load: () => Promise<void>;
  upload: (file: File) => Promise<AssetDto | null>;
  clearError: () => void;
}

export const useAssetStore = create<AssetState>()((set, get) => ({
  assets: {},
  loaded: false,
  uploading: false,
  error: null,
  async load() {
    try {
      const { assets } = await assetsApi.list();
      set({ assets: Object.fromEntries(assets.map((a) => [a.id, a])), loaded: true });
    } catch (err) {
      set({ error: userMessage(err) });
    }
  },
  async upload(file) {
    const { m, fmt } = currentI18n();
    if (!/\.glb$/i.test(file.name)) {
      set({ error: m.editor.messages.glbOnly });
      return null;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      set({ error: fmt(m.editor.messages.tooLarge, { mb: MAX_UPLOAD_BYTES / 1024 / 1024 }) });
      return null;
    }
    set({ uploading: true, error: null });
    try {
      const { asset } = await assetsApi.upload(file);
      set({ assets: { ...get().assets, [asset.id]: asset }, uploading: false });
      return asset;
    } catch (err) {
      set({ uploading: false, error: userMessage(err) });
      return null;
    }
  },
  clearError: () => set({ error: null }),
}));
